import React from 'react';
import styled, { css, keyframes, type DefaultTheme } from 'styled-components';
import { useErrorNotification, ErrorNotification as ErrorNotificationData } from '../../contexts/ErrorNotificationContext';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideInMobile = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const slideOutMobile = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const NotificationContainer = styled.div`
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.mobile.xs};
  pointer-events: none; /* Allow clicking through container */
  
  /* Mobile-first: bottom positioning for better reachability */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    bottom: 0;
    left: 0;
    right: 0;
    padding: ${({ theme }) => theme.spacing.mobile.gutter};
    padding-bottom: max(${({ theme }) => theme.spacing.mobile.gutter}, env(safe-area-inset-bottom));
    
    /* Enhanced z-index for mobile navigation */
    z-index: 10001;
    
    /* Ensure proper spacing from mobile UI elements */
    margin-bottom: max(${({ theme }) => theme.spacing.mobile.sm}, env(safe-area-inset-bottom));
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    bottom: 0;
    left: ${({ theme }) => theme.spacing.md};
    right: ${({ theme }) => theme.spacing.md};
    padding-bottom: max(${({ theme }) => theme.spacing.lg}, env(safe-area-inset-bottom));
    
    z-index: 10001;
    max-width: none;
  }
  
  /* Enhanced iPhone X+ safe area support with fallbacks */
  @supports (padding: max(0px)) {
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      padding-left: max(${({ theme }) => theme.spacing.mobile.gutter}, env(safe-area-inset-left));
      padding-right: max(${({ theme }) => theme.spacing.mobile.gutter}, env(safe-area-inset-right));
      padding-bottom: max(${({ theme }) => theme.spacing.mobile.lg}, env(safe-area-inset-bottom));
    }
  }
  
  /* Enhanced desktop: top-right positioning with better spacing */
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    top: ${({ theme }) => theme.spacing.gallery.lg};
    right: ${({ theme }) => theme.spacing.gallery.sm};
    bottom: auto;
    left: auto;
    width: 420px;
    max-width: calc(100vw - ${({ theme }) => theme.spacing.gallery.md});
    padding: 0;
    margin: 0;
    
    /* Standard desktop z-index */
    z-index: 9999;
  }
  
  /* Large desktop improvements */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    top: ${({ theme }) => theme.spacing.gallery.xl};
    right: ${({ theme }) => theme.spacing.gallery.lg};
    width: 480px;
  }
`;

const getErrorColors = (type: ErrorNotificationData['type'], theme: DefaultTheme) => {
  switch (type) {
    case 'auth':
      return {
        background: theme.colors.error + '10',
        border: theme.colors.error,
        icon: '🔐',
        color: theme.colors.error
      };
    case 'network':
      return {
        background: theme.colors.warning + '10',
        border: theme.colors.warning,
        icon: '🌐',
        color: theme.colors.warning
      };
    case 'validation':
      return {
        background: theme.colors.warning + '10',
        border: theme.colors.warning,
        icon: '⚠️',
        color: theme.colors.warning
      };
    case 'server':
      return {
        background: theme.colors.error + '10',
        border: theme.colors.error,
        icon: '🔧',
        color: theme.colors.error
      };
    default:
      return {
        background: theme.colors.gray.light,
        border: theme.colors.gray.medium,
        icon: 'ℹ️',
        color: theme.colors.text
      };
  }
};

const NotificationCard = styled.div<{ 
  $type: ErrorNotificationData['type'];
  $isVisible: boolean;
  $translateX: number;
}>`
  background: ${({ theme }) => theme.colors.white};
  border-left: 4px solid ${({ theme, $type }) => getErrorColors($type, theme).border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 16px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  transform: translateX(${({ $translateX }) => $translateX}px);
  transition: transform ${({ theme }) => theme.transitions.gallery.instant};
  position: relative;
  pointer-events: auto; /* Re-enable pointer events for cards */
  
  /* Desktop animations */
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    animation: ${({ $isVisible }) => $isVisible ? slideIn : slideOut} 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  /* Mobile animations - slide up from bottom */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    animation: ${({ $isVisible }) => $isVisible ? slideInMobile : slideOutMobile} 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  ${({ theme, $type }) => {
    const colors = getErrorColors($type, theme);
    return css`
      background: ${colors.background};
      border-color: ${colors.border};
    `;
  }}

  /* Enhanced mobile styling */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin: 0;
    padding: ${({ theme }) => theme.spacing.mobile.padding};
    touch-action: pan-x;
    cursor: grab;
    border-radius: ${({ theme }) => theme.borderRadius.gallery.comfortable};
    box-shadow: ${({ theme }) => theme.shadows.gallery.comfortable};
    
    /* Enhanced mobile border */
    border: 1px solid ${({ theme, $type }) => getErrorColors($type, theme).border}30;
    border-left: 4px solid ${({ theme, $type }) => getErrorColors($type, theme).border};
    
    /* Better backdrop */
    background: ${({ theme }) => theme.colors.mobile.surface};
    backdrop-filter: blur(8px);
    
    &:active {
      cursor: grabbing;
      transform: translateX(${({ $translateX }) => $translateX}px) scale(0.99);
    }
    
    /* Enhanced swipe indicator */
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      right: -2px;
      width: 4px;
      height: 32px;
      background: linear-gradient(to bottom, 
        ${({ theme, $type }) => getErrorColors($type, theme).border}60,
        ${({ theme, $type }) => getErrorColors($type, theme).border}40,
        ${({ theme, $type }) => getErrorColors($type, theme).border}60
      );
      border-radius: ${({ theme }) => theme.borderRadius.sm};
      transform: translateY(-50%);
      transition: all ${({ theme }) => theme.transitions.gallery.quick};
      opacity: ${({ $translateX }) => Math.min($translateX / 15, 1)};
      box-shadow: 0 0 8px ${({ theme, $type }) => getErrorColors($type, theme).border}20;
    }
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin: 0;
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
    touch-action: pan-x;
    cursor: grab;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    box-shadow: ${({ theme }) => theme.shadows.xl};
    
    &:active {
      cursor: grabbing;
    }
    
    border: 1px solid ${({ theme, $type }) => getErrorColors($type, theme).border}25;
    border-left: 4px solid ${({ theme, $type }) => getErrorColors($type, theme).border};
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      right: -3px;
      width: 3px;
      height: 28px;
      background: ${({ theme, $type }) => getErrorColors($type, theme).border}50;
      border-radius: 2px;
      transform: translateY(-50%);
      transition: opacity ${({ theme }) => theme.transitions.gallery.quick};
      opacity: ${({ $translateX }) => Math.min($translateX / 18, 1)};
    }
  }
  
  /* Enhanced desktop styling */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
    box-shadow: ${({ theme }) => theme.shadows.gallery.soft};
    transition: all ${({ theme }) => theme.transitions.gallery.hover};
    
    &:hover {
      box-shadow: ${({ theme }) => theme.shadows.gallery.comfortable};
      transform: translateX(${({ $translateX }) => $translateX}px) translateY(-1px);
    }
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 767px) {
    margin-bottom: ${({ theme }) => theme.spacing.md};
    gap: ${({ theme }) => theme.spacing.md};
    /* Ensure proper alignment on mobile */
    align-items: center;
  }
`;

const NotificationTitle = styled.div<{ $type: ErrorNotificationData['type'] }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme, $type }) => getErrorColors($type, theme).color};
  
  &::before {
    content: '${({ $type, theme }) => getErrorColors($type, theme).icon}';
    font-size: 16px;
  }

  @media (max-width: 767px) {
    font-size: ${({ theme }) => theme.typography.mobile.heading};
    line-height: ${({ theme }) => theme.typography.lineHeight.snug};
    flex: 1; /* Allow title to use available space */
  }
`;

const NotificationMessage = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};

  @media (max-width: 767px) {
    font-size: ${({ theme }) => theme.typography.mobile.body};
    line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    /* Better text wrapping on mobile */
    word-wrap: break-word;
    hyphens: auto;
  }
`;

const NotificationActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 767px) {
    gap: ${({ theme }) => theme.spacing.md};
    justify-content: stretch;
    /* Stack buttons vertically on small screens for better touch targets */
    flex-direction: column;
  }

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  padding: 6px 12px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  ${({ theme, $variant }) => $variant === 'primary' ? css`
    background-color: ${theme.colors.primary};
    color: ${theme.colors.white};
    
    &:hover {
      background-color: ${theme.colors.primary}dd;
    }
  ` : css`
    background-color: transparent;
    color: ${theme.colors.text};
    border: 1px solid ${theme.colors.gray.medium};
    
    &:hover {
      background-color: ${theme.colors.gray.light};
    }
  `}

  &:active {
    transform: translateY(1px);
  }

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
    font-size: ${({ theme }) => theme.typography.mobile.body};
    min-height: ${({ theme }) => theme.breakpoints.touchTarget};
    touch-action: manipulation;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    
    /* Full width on mobile for easier tapping */
    width: 100%;
    
    /* Better visual feedback on mobile */
    &:active {
      transform: translateY(2px);
      transition: transform 0.1s ease-out;
    }
  }
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.gray.dark};
  cursor: pointer;
  padding: 4px;
  font-size: 18px;
  line-height: 1;
  transition: all ${({ theme }) => theme.transitions.fast};
  min-width: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  
  &:hover {
    color: ${({ theme }) => theme.colors.text};
    background-color: ${({ theme }) => theme.colors.gray.light}20;
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 767px) {
    font-size: 20px;
    min-width: 44px; /* Accessibility touch target */
    min-height: 44px;
    padding: 8px;
    
    &:hover {
      background-color: ${({ theme }) => theme.colors.gray.light}30;
    }
  }
`;

interface SingleNotificationProps {
  notification: ErrorNotificationData;
  onDismiss: (id: string) => void;
}

function SingleNotification({ notification, onDismiss }: SingleNotificationProps) {
  const [translateX, setTranslateX] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [startX, setStartX] = React.useState(0);
  const [startY, setStartY] = React.useState(0);
  const [isVerticalScroll, setIsVerticalScroll] = React.useState(false);
  const [dismissThreshold] = React.useState(80); // Dynamic threshold
  const [velocity, setVelocity] = React.useState(0);
  const lastMoveTime = React.useRef(Date.now());
  const lastMoveX = React.useRef(0);

  const handleDismiss = () => {
    onDismiss(notification.id);
  };

  const handleRetry = () => {
    notification.onRetry?.();
    handleDismiss();
  };

  // Enhanced touch event handlers with better gesture recognition
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setStartX(touch.clientX);
    setStartY(touch.clientY);
    setIsVerticalScroll(false);
    lastMoveTime.current = Date.now();
    lastMoveX.current = touch.clientX;
    setVelocity(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    
    const touch = e.touches[0];
    const currentX = touch.clientX;
    const currentY = touch.clientY;
    const deltaX = currentX - startX;
    const deltaY = currentY - startY;
    const now = Date.now();
    const timeDiff = now - lastMoveTime.current;
    
    // Calculate velocity for better gesture recognition
    if (timeDiff > 0) {
      const velocityX = (currentX - lastMoveX.current) / timeDiff;
      setVelocity(velocityX);
      lastMoveTime.current = now;
      lastMoveX.current = currentX;
    }
    
    // Detect if user is trying to scroll vertically
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 10) {
      setIsVerticalScroll(true);
      return;
    }
    
    // Ignore vertical scrolling
    if (isVerticalScroll) return;
    
    // Prevent default to avoid interfering with scroll
    if (Math.abs(deltaX) > 10) {
      e.preventDefault();
    }
    
    // Allow both left and right swipe, but prioritize right swipe for dismiss
    if (deltaX > 0) {
      // Right swipe - dismiss gesture
      setTranslateX(Math.min(deltaX, 120)); // Increased cap for better UX
    } else if (deltaX < -20) {
      // Left swipe - small resistance feedback
      setTranslateX(Math.max(deltaX / 4, -30)); // Limited left movement
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    setIsVerticalScroll(false);
    
    // Enhanced dismiss logic considering velocity
    const shouldDismiss = translateX > dismissThreshold || 
                         (translateX > 40 && velocity > 0.3) || // Fast swipe
                         translateX > dismissThreshold * 0.7; // 70% of threshold
    
    if (shouldDismiss && translateX > 0) {
      // Animate out before dismissing
      setTranslateX(200);
      setTimeout(() => {
        handleDismiss();
      }, 150);
    } else {
      // Smooth snap back with spring-like animation
      setTranslateX(0);
    }
  };

  // Enhanced mouse event handlers for desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only on smaller screens or when explicitly desired
    if (window.innerWidth > 768) return;
    
    e.preventDefault();
    setIsDragging(true);
    setStartX(e.clientX);
    lastMoveTime.current = Date.now();
    lastMoveX.current = e.clientX;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || window.innerWidth > 768) return;
    
    const deltaX = e.clientX - startX;
    const now = Date.now();
    const timeDiff = now - lastMoveTime.current;
    
    if (timeDiff > 0) {
      const velocityX = (e.clientX - lastMoveX.current) / timeDiff;
      setVelocity(velocityX);
      lastMoveTime.current = now;
      lastMoveX.current = e.clientX;
    }
    
    if (deltaX > 0) {
      setTranslateX(Math.min(deltaX, 120));
    } else {
      setTranslateX(Math.max(deltaX / 4, -30));
    }
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    const shouldDismiss = translateX > dismissThreshold || 
                         (translateX > 40 && velocity > 0.3);
    
    if (shouldDismiss && translateX > 0) {
      setTranslateX(200);
      setTimeout(() => {
        handleDismiss();
      }, 150);
    } else {
      setTranslateX(0);
    }
  };

  // Add keyboard support for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleDismiss();
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (notification.onRetry && notification.isRetryable) {
        handleRetry();
      } else {
        handleDismiss();
      }
      e.preventDefault();
    }
  };

  return (
    <NotificationCard 
      $type={notification.type}
      $isVisible={true}
      $translateX={translateX}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={isDragging ? handleMouseMove : undefined}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <NotificationHeader>
        <NotificationTitle $type={notification.type}>
          {notification.title}
        </NotificationTitle>
        <DismissButton 
          onClick={handleDismiss}
          aria-label="Dismiss notification"
        >
          ×
        </DismissButton>
      </NotificationHeader>
      
      <NotificationMessage>
        {notification.message}
      </NotificationMessage>
      
      <NotificationActions>
        {notification.onRetry && notification.isRetryable && (
          <ActionButton 
            $variant="primary" 
            onClick={handleRetry}
            aria-label="Retry the failed action"
          >
            Retry
          </ActionButton>
        )}
        <ActionButton 
          $variant="secondary" 
          onClick={handleDismiss}
          aria-label="Dismiss this notification"
        >
          Dismiss
        </ActionButton>
      </NotificationActions>
      
      {/* Mobile swipe hint for first-time users */}
      {translateX === 0 && !isDragging && (
        <div 
          style={{ 
            position: 'absolute',
            bottom: '4px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '10px',
            color: '#999',
            opacity: 0.7,
            pointerEvents: 'none',
            display: window.innerWidth <= 768 ? 'block' : 'none'
          }}
          aria-hidden="true"
        >
          Swipe right to dismiss
        </div>
      )}
    </NotificationCard>
  );
}

export function ErrorNotificationDisplay() {
  const { notifications, dismissError } = useErrorNotification();

  if (notifications.length === 0) {
    return null;
  }

  return (
    <NotificationContainer>
      {notifications.map((notification) => (
        <SingleNotification
          key={notification.id}
          notification={notification}
          onDismiss={dismissError}
        />
      ))}
    </NotificationContainer>
  );
}

export default ErrorNotificationDisplay;